import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chevron, XGlyph, IdentityPill, LetterSquare, LISTED_GRAY } from './proUi';
import useMediaQuery from '../../utils/useMediaQuery';
import usePageScrollLock from '../../utils/usePageScrollLock';
import useSwipeToDismiss from '../../hooks/useSwipeToDismiss';

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Gallery viewers (handoff §6). ≥640: a lightbox — black surface, whole photo in a
 * 2px white frame, chevrons each side, a filmstrip footer. <640: a story viewer —
 * full-bleed photo, segment bars with a part-filled current segment, tap zones,
 * hold to pause.
 */
export default function ProGallery({ photos, index = 0, businessName, onClose, onOffer, offerLabel = 'SEND AN OFFER' }) {
  const mobile = useMediaQuery('(max-width: 639px)');
  const [i, setI] = useState(Math.max(0, Math.min(index, photos.length - 1)));
  const n = photos.length;
  const prev = useCallback(() => setI((x) => (x - 1 + n) % n), [n]);
  const next = useCallback(() => setI((x) => (x + 1) % n), [n]);

  usePageScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowLeft') prev(); if (e.key === 'ArrowRight') next(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  if (n === 0) return null;
  const current = photos[i];
  const counter = `${pad2(i + 1)} / ${pad2(n)}`;

  if (mobile) {
    return <StoryViewer photos={photos} i={i} setI={setI} next={next} prev={prev} counter={counter} businessName={businessName} onClose={onClose} onOffer={onOffer} offerLabel={offerLabel} />;
  }

  return (
    <div className="pr-lightbox fixed inset-0 z-[9999] flex flex-col bg-black font-mono text-white" role="dialog" aria-modal="true" aria-label={`${businessName} photos`} data-testid="pro-lightbox">
      <div className="m-3 flex min-h-0 flex-1 flex-col border-2 border-white">
        <div className="flex items-center justify-between border-b-2 border-white" style={{ padding: '16px 20px' }}>
          <span className="font-bold uppercase" style={{ fontSize: 13, letterSpacing: '0.1em' }}>{businessName}</span>
          <div className="flex items-center gap-5">
            <span style={{ fontSize: 12, color: LISTED_GRAY }}>{counter}</span>
            <button type="button" onClick={onClose} aria-label="Close" className="hover:opacity-70"><XGlyph size={16} /></button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center gap-5 p-5">
          <button type="button" onClick={prev} aria-label="Previous" className="flex flex-none items-center justify-center border-2 border-white transition-colors hover:bg-white hover:text-black" style={{ width: 56, height: 56 }}>
            <Chevron size={16} rotate={90} />
          </button>
          <div className="flex min-h-0 flex-1 items-center justify-center self-stretch">
            <img src={current.url} alt={current.caption || ''} className="max-h-full max-w-full border-2 border-white object-contain" />
          </div>
          <button type="button" onClick={next} aria-label="Next" className="flex flex-none items-center justify-center border-2 border-white transition-colors hover:bg-white hover:text-black" style={{ width: 56, height: 56 }}>
            <Chevron size={16} rotate={-90} />
          </button>
        </div>
        <div className="flex gap-1 border-t-2 border-white p-3" style={{ height: 80 }}>
          {photos.map((p, k) => (
            <button key={p.id || k} type="button" onClick={() => setI(k)} aria-label={`Photo ${k + 1}`} aria-current={k === i}
              className="min-w-0 flex-1 overflow-hidden border-2" style={{ height: 56, borderColor: k === i ? '#FFFFFF' : '#6B7280', background: '#1A1A1A', opacity: k === i ? 1 : 0.7 }}>
              <img src={p.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const STORY_MS = 5000;

function StoryViewer({ photos, i, setI, next, prev, counter, businessName, onClose, onOffer, offerLabel }) {
  const [progress, setProgress] = useState(0); // 0..1 within the current segment
  const paused = useRef(false);
  const holdTimer = useRef(null);
  const last = useRef(null);

  useEffect(() => { setProgress(0); }, [i]);

  useEffect(() => {
    let raf;
    const tick = (t) => {
      if (last.current == null) last.current = t;
      const dt = t - last.current; last.current = t;
      if (!paused.current) {
        setProgress((p) => {
          const np = p + dt / STORY_MS;
          if (np >= 1) {
            if (i >= photos.length - 1) { onClose(); return 1; }
            setI(i + 1); return 0;
          }
          return np;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); last.current = null; };
  }, [i, photos.length, setI, onClose]);

  // Swipe down to close. The stage here IS two tap-zone buttons, so they are marked
  // `data-swipe-ok` — without that the hook would read every touch as a control's and
  // never engage.
  const frameRef = useRef(null);
  const swipe = useSwipeToDismiss(frameRef, { onDismiss: () => onClose?.() });

  const down = () => { holdTimer.current = setTimeout(() => { paused.current = true; }, 200); };
  const up = (zone) => () => {
    clearTimeout(holdTimer.current);
    if (swipe.claimed.current) { paused.current = false; return; }
    if (paused.current) { paused.current = false; return; }
    if (zone === 'left') prev(); else next();
  };

  return (
    <div ref={frameRef} className="pr-lightbox fixed inset-0 z-[9999] flex flex-col bg-black font-mono text-white" role="dialog" aria-modal="true" aria-label={`${businessName} photos`} data-testid="pro-story" style={swipe.style}>
      <img src={photos[i].url} alt={photos[i].caption || ''} className="absolute inset-0 h-full w-full object-cover" />
      {/* Top band */}
      <div className="pt-safe-top relative z-10 bg-black" style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12 }}>
        <div className="flex" style={{ gap: 3 }} aria-hidden="true">
          {photos.map((p, k) => (
            <div key={p.id || k} className="flex-1" style={{ height: 3, background: '#6B7280' }}>
              <div style={{ height: '100%', background: '#FFFFFF', width: k < i ? '100%' : k === i ? `${Math.round(progress * 100)}%` : '0%' }} data-testid={k === i ? 'story-current' : undefined} />
            </div>
          ))}
        </div>
        <div className="mt-2.5 flex items-center gap-2.5">
          <LetterSquare name={businessName} size={28} fontSize={14} className="border-2 border-white" />
          <span className="min-w-0 flex-1 truncate font-bold" style={{ fontSize: 13 }}>{businessName}</span>
          <IdentityPill listed={false} />
          <button type="button" onClick={onClose} aria-label="Close" className="ml-1"><XGlyph size={16} /></button>
        </div>
      </div>
      {/* Tap zones */}
      <div className="relative z-10 flex flex-1">
        <button type="button" aria-label="Previous" data-swipe-ok className="w-1/3" onPointerDown={down} onPointerUp={up('left')} />
        <button type="button" aria-label="Next" data-swipe-ok className="w-2/3" onPointerDown={down} onPointerUp={up('right')} />
      </div>
      {/* Bottom bar */}
      <div className="pb-safe-bot relative z-10 flex items-center justify-between bg-black" style={{ paddingTop: 12, paddingLeft: 14, paddingRight: 14, '--pb-base': '12px' }}>
        <span style={{ fontSize: 12, color: LISTED_GRAY }}>{counter}</span>
        {onOffer && (
          <button type="button" onClick={onOffer} className="border-2 border-white bg-white font-bold uppercase text-black" style={{ fontSize: 11, letterSpacing: '0.05em', padding: '8px 14px' }}>{offerLabel}</button>
        )}
      </div>
    </div>
  );
}
